import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classes from './Select.module.scss';
import cn from 'classnames';
import { ReactComponent as DropdownArrow } from '../../../assets/icons/arrow-dropdown.svg';
import Option from './Option/Option';
import { useEscapeClick } from '../../../utils/escapeClick';
import { useOutsideClick } from '../../../utils/outsideClick';
import InputWrapper, { InputWrapperProps } from '../Input/Wrapper/InputWrapper';

export interface SelectOption {
  value: string;
  label?: string;
  additionalLabel?: string;
}

export interface SelectProps extends InputWrapperProps {
  options: Array<SelectOption>;
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  optionsWrapperClassName?: string;
  optionsClassName?: string;
  optionsExtra?: JSX.Element | null;
  optionsExtraClassName?: string;
  optionClassName?: string;
  dropDownIconClassName?: string;
  onChangeAction: (val: string, index?: number) => void;
  icon?: JSX.Element | null;
  dropDownIcon?: JSX.Element | null;
}

function Select(props: SelectProps) {
  const {
    wrapperClassName,
    inputClassName,
    optionsWrapperClassName,
    optionsClassName,
    optionClassName,
    options,
    value,
    defaultValue = '',
    placeholder,
    optionsExtra,
    optionsExtraClassName,
    onChangeAction,
    icon,
    dropDownIcon,
    error,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>(defaultValue);
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick(ref, () => setIsOpen(false));
  useEscapeClick(ref, () => setIsOpen(false));

  const setOption = (selectOption: SelectOption) => {
    if (selectOption.additionalLabel) {
      setSelectedOption(selectOption.additionalLabel);
    }
    if (selectOption.label && !selectOption.additionalLabel) {
      setSelectedOption(selectOption.label);
    }
    if (!selectOption.label && !selectOption.additionalLabel) {
      setSelectedOption(selectOption.value);
    }
  };

  const setSelectValue = useCallback((value: SelectOption) => {
    setOption(value);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const val = options.find(option => option.value === value);
    if (val) {
      setSelectValue(val);
    }
  }, [setSelectValue, options, value]);

  const optionClickHandler = useCallback(
    (val: SelectOption, index?: number) => {
      setSelectValue(val);
      onChangeAction(val.value, index);
    },
    [onChangeAction, setSelectValue],
  );

  const dropDownArrow = useMemo(() => {
    if (dropDownIcon) {
      return dropDownIcon;
    }
    return <DropdownArrow height={'0.8rem'} width={'0.8rem'} />;
  }, [dropDownIcon]);

  const inputMixedClassName = cn(classes.input, inputClassName, error && classes.inputError);

  return (
    <InputWrapper ref={ref} wrapperClassName={cn(classes.wrapper, wrapperClassName)} error={error} {...rest}>
      <div className={inputMixedClassName} onClick={() => setIsOpen(!isOpen)}>
        {!!icon && <div className={classes.iconContainer}>{icon}</div>}
        <span className={classes.value}>{selectedOption || placeholder}</span>
        <div className={cn(classes.dropDownIconWrapper, isOpen && classes.isOpen)}>{dropDownArrow}</div>
      </div>
      {isOpen && (
        <div className={cn(classes.optionsWrapper, optionsWrapperClassName)}>
          <div className={cn(classes.options, optionsClassName)}>
            {options.map((option, index) => (
              <Option
                option={option}
                className={cn(classes.option, optionClassName)}
                clickAction={it => optionClickHandler(it, index)}
                key={option.value}
                selected={option.value === value}
              >
                {option.label || option.value}
              </Option>
            ))}
          </div>
          {!!optionsExtra && <div className={optionsExtraClassName}>{optionsExtra}</div>}
        </div>
      )}
    </InputWrapper>
  );
}

export default Select;
